import React, { ReactNode } from 'react';
import { ReportExportColumn } from '@core/utils/methods/report-export-table-data';
import {
  OptionProps,
  ReportsFilters,
  ReportsSortBy,
  StructuralPassFailRow,
} from '@models/interfaces';
import {
  getStructuralStatus as deriveStructuralStatus,
  getStructuralStatusClassName as getDerivedStructuralStatusClassName,
  getStructuralStatusLabel as getDerivedStructuralStatusLabel,
} from '../structural-pass-fail-helpers';
import {
  getStructuralSortableColumn,
  renderBooleanValue,
  renderDateValue,
  renderPercentValue,
  renderValue,
} from './common';

const renderStructuralStatus = (
  _value: unknown,
  record: StructuralPassFailRow,
): ReactNode => {
  const status = deriveStructuralStatus(record);

  return React.createElement(
    'span',
    { className: getDerivedStructuralStatusClassName(status) },
    getDerivedStructuralStatusLabel(status),
  );
};

export const getStructuralPassFailColumns = (
  filters: ReportsFilters,
  sortByOptions: OptionProps[],
): ReportExportColumn<StructuralPassFailRow>[] => [
  {
    key: 'structuralStatus',
    exportValue: record =>
      getDerivedStructuralStatusLabel(deriveStructuralStatus(record)),
    render: renderStructuralStatus,
    title: 'Structural Status',
  },
  getStructuralSortableColumn(
    'projectBundleID',
    'Project Bundle ID',
    ReportsSortBy.ProjectBundleID,
    filters,
    sortByOptions,
    renderValue,
  ),
  {
    dataIndex: 'oracleProjectNumbersDisplay',
    key: 'oracleProjectNumbersDisplay',
    render: renderValue,
    title: 'Oracle Project Numbers',
  },
  {
    dataIndex: 'iwmJobNumbersDisplay',
    key: 'iwmJobNumbersDisplay',
    title: 'IWM Job Numbers',
  },
  {
    dataIndex: 'paceProjectNumbersDisplay',
    key: 'paceProjectNumbersDisplay',
    render: renderValue,
    title: 'PACE Project Numbers',
  },
  getStructuralSortableColumn(
    'projectType',
    'Project Type',
    ReportsSortBy.ProjectType,
    filters,
    sortByOptions,
    renderValue,
  ),
  {
    dataIndex: 'projectSubtype',
    key: 'projectSubtype',
    render: renderValue,
    title: 'Project Subtype',
  },
  getStructuralSortableColumn(
    'projectStatus',
    'Project Status',
    ReportsSortBy.ProjectStatus,
    filters,
    sortByOptions,
    renderValue,
  ),
  getStructuralSortableColumn(
    'progressStatus',
    'Progress Status',
    ReportsSortBy.ProgressStatus,
    filters,
    sortByOptions,
    renderValue,
  ),
  getStructuralSortableColumn(
    'siteNumber',
    'Site Number',
    ReportsSortBy.SiteNumber,
    filters,
    sortByOptions,
    renderValue,
  ),
  {
    dataIndex: 'FALocationNumber',
    key: 'FALocationNumber',
    render: renderValue,
    title: 'FA Location Number',
  },
  {
    dataIndex: 'city',
    key: 'city',
    render: renderValue,
    title: 'City',
  },
  {
    dataIndex: 'state',
    key: 'state',
    render: renderValue,
    title: 'State',
  },
  {
    dataIndex: 'submarket',
    key: 'submarket',
    render: renderValue,
    title: 'Submarket',
  },
  {
    dataIndex: 'towerOwner',
    key: 'towerOwner',
    render: renderValue,
    title: 'Tower Owner',
  },
  {
    dataIndex: 'structuralRequired',
    key: 'structuralRequired',
    render: renderBooleanValue,
    title: 'Structural Required',
  },
  {
    dataIndex: 'structuralOrderedDate',
    key: 'structuralOrderedDate',
    render: renderDateValue,
    title: 'Structural Ordered',
  },
  {
    dataIndex: 'passingStructuralReceivedDate',
    key: 'passingStructuralReceivedDate',
    render: renderDateValue,
    title: 'Passing Structural Received',
  },
  {
    dataIndex: 'failingStructuralReceivedDate',
    key: 'failingStructuralReceivedDate',
    render: renderDateValue,
    title: 'Failing Structural Received',
  },
  getStructuralSortableColumn(
    'structuralResult',
    'Structural Result',
    ReportsSortBy.StructuralResult,
    filters,
    sortByOptions,
    renderValue,
  ),
  getStructuralSortableColumn(
    'towerPercent',
    'Tower %',
    ReportsSortBy.TowerPercent,
    filters,
    sortByOptions,
    renderPercentValue,
  ),
  getStructuralSortableColumn(
    'foundationPercent',
    'Foundation %',
    ReportsSortBy.FoundationPercent,
    filters,
    sortByOptions,
    renderPercentValue,
  ),
];

export const getStructuralPassFailReportRowClassName = (
  record: StructuralPassFailRow,
): string =>
  record.projectBundleID
    ? 'prov-structural-pass-fail-report__row--clickable'
    : 'prov-structural-pass-fail-report__row--disabled';

export const getStructuralPassFailReportRowKey = (
  record: StructuralPassFailRow,
): string =>
  [record.projectId, record.projectBundleID].filter(Boolean).join('-');
